import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Alert,
  Typography,
  Chip,
  Paper,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import {
  DndContext,
  pointerWithin,
  rectIntersection,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  useDroppable,
  CollisionDetection,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FormSection, QuestionField, FieldOption, QuestionnairePopup } from '../../types/questionnaire.types';
import { FieldEditDialog } from './FieldEditDialog';

interface SectionEditorProps {
  section: FormSection;
  onUpdate: (section: FormSection) => void;
  allFields?: Array<{ name: string; label: string; type: string; options?: FieldOption[] }>;
  availablePopups?: QuestionnairePopup[];
  questionnaireTypeId?: number | null;  // Used to scope email-verification template options to this questionnaire
  /** The questionnaire's own type ('create_lead', 'create_patient', ...).
   *  Decides whether the field dialog offers a lead mapping at all. */
  questionnaireKind?: string;
  language?: 'en' | 'he';  // Forwarded to the field editor so Hebrew inputs read right-to-left
}

interface SortableFieldItemProps {
  field: QuestionField;
  fieldIndex: number;
  parentIndex?: number | null;
  onEdit: (field: QuestionField, index: number, parentIndex?: number | null) => void;
  onClone?: (field: QuestionField, index: number, parentIndex?: number | null) => void;
  onDelete: () => void;
  isNested?: boolean;
}

interface LineFieldContainerProps {
  field: QuestionField;
  fieldIndex: number;
  onEdit: (field: QuestionField, index: number) => void;
  onDelete: () => void;
  onEditNested: (field: QuestionField, nestedIndex: number, parentIndex: number | null | undefined) => void;
  onCloneNested: (field: QuestionField, nestedIndex: number, parentIndex: number | null | undefined) => void;
  onDeleteNested: (nestedIndex: number) => void;
  onAddNested: () => void;
}

interface NestedLineFieldContainerProps {
  field: QuestionField;
  containerIndex: number;
  lineIndex: number;
  dropId: string;
  onAddNestedLineField: (containerIndex: number, lineIndex: number) => void;
  onEditNested: (field: QuestionField, nestedIndex: number, parentIndex: number | null | undefined) => void;
  onDeleteNested: (nestedIndex: number) => void;
  onEditNestedLineField: (containerIndex: number, lineIndex: number, field: QuestionField, fieldIndex: number) => void;
  onDeleteNestedLineField: (containerIndex: number, lineIndex: number, fieldIndex: number) => void;
}

interface ContainerFieldContainerProps {
  field: QuestionField;
  fieldIndex: number;
  onEdit: (field: QuestionField, index: number) => void;
  onDelete: () => void;
  onEditNested: (field: QuestionField, nestedIndex: number, parentIndex: number | null | undefined) => void;
  onCloneNested: (field: QuestionField, nestedIndex: number, parentIndex: number | null | undefined) => void;
  onDeleteNested: (nestedIndex: number) => void;
  onAddNested: () => void;
  // Handlers for managing fields inside nested line fields
  onAddNestedLineField: (containerIndex: number, lineIndex: number) => void;
  onEditNestedLineField: (containerIndex: number, lineIndex: number, field: QuestionField, fieldIndex: number) => void;
  onDeleteNestedLineField: (containerIndex: number, lineIndex: number, fieldIndex: number) => void;
}

const SortableFieldItem: React.FC<SortableFieldItemProps> = ({
  field,
  fieldIndex,
  parentIndex,
  onEdit,
  onClone,
  onDelete,
  isNested = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: field.name,
    data: {
      type: isNested ? 'nested-field' : 'top-level-field',
      parentIndex,
      fieldIndex,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (isNested) {
    return (
      <Paper
        ref={setNodeRef}
        style={style}
        elevation={1}
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          p: 1,
          bgcolor: '#F9FAFB',
          gap: 1,
          minHeight: '48px',
        }}
      >
        <IconButton
          {...attributes}
          {...listeners}
          size="small"
          sx={{ cursor: isDragging ? 'grabbing' : 'grab', p: 0.5 }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.875rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {field.label}
            {field.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            {field.type}
          </Typography>
        </Box>
        <IconButton
          onClick={() => onEdit(field, fieldIndex, parentIndex)}
          size="small"
          sx={{ p: 0.5 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        {onClone && (
          <IconButton
            onClick={() => onClone(field, fieldIndex, parentIndex)}
            size="small"
            sx={{ p: 0.5 }}
            title="Clone question"
          >
            <CloneIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton
          onClick={onDelete}
          color="error"
          size="small"
          sx={{ p: 0.5 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Paper>
    );
  }

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        border: '1px solid #E5E7EB',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        p: 2,
        bgcolor: 'transparent',
      }}
    >
      <IconButton
        {...attributes}
        {...listeners}
        size="small"
        sx={{ cursor: isDragging ? 'grabbing' : 'grab', mr: 1 }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <ListItemText
        primary={(() => {
          // For button-group with empty label, show HTML content preview (text only, no lists)
          if (field.type === 'button-group' && (!field.label || !field.label.trim()) && (field as any).htmlContent) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = (field as any).htmlContent;
            // Remove list elements
            tempDiv.querySelectorAll('ul, ol').forEach(el => el.remove());
            // Get text content
            const textContent = tempDiv.textContent || tempDiv.innerText || '';
            return textContent.trim().substring(0, 100) + (textContent.length > 100 ? '...' : '');
          }
          return field.label;
        })()}
        secondary={`Type: ${field.type}${field.required ? ' (Required)' : ''}`}
      />
      <Box>
        <IconButton
          onClick={() => onEdit(field, fieldIndex, parentIndex)}
          sx={{ mr: 0.5 }}
          size="small"
        >
          <EditIcon fontSize="small" />
        </IconButton>
        {onClone && (
          <IconButton
            onClick={() => onClone(field, fieldIndex, parentIndex)}
            sx={{ mr: 0.5 }}
            size="small"
            title="Clone question"
          >
            <CloneIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton
          onClick={onDelete}
          color="error"
          size="small"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </ListItem>
  );
};

const LineFieldContainer: React.FC<LineFieldContainerProps> = ({
  field,
  fieldIndex,
  onEdit,
  onDelete,
  onEditNested,
  onCloneNested,
  onDeleteNested,
  onAddNested,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.name });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `line-${field.name}`,
    data: { type: 'line-container', parentIndex: fieldIndex },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const nestedFields = field.nestedFields || [];

  return (
    <ListItem
      ref={setDragRef}
      style={style}
      sx={{
        mb: 1,
        border: isOver ? '2px solid #2e5c66' : '2px solid #3A7380',
        borderRadius: '20px',
        display: 'block',
        p: 2,
        bgcolor: isOver ? '#E0F2F1' : '#F0F7F8',
        transition: 'all 0.2s ease',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton
          {...attributes}
          {...listeners}
          size="small"
          sx={{ cursor: isDragging ? 'grabbing' : 'grab', mr: 1 }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {field.label}
              <Chip
                label="Line (Side by Side)"
                size="small"
                sx={{ bgcolor: '#3A7380', color: 'white', fontSize: '0.7rem', height: '20px' }}
              />
            </Box>
          }
          secondary={`Type: ${field.type}${field.required ? ' (Required)' : ''}`}
        />
        <Box>
          <IconButton
            onClick={() => onEdit(field, fieldIndex)}
            sx={{ mr: 1 }}
            size="small"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={onDelete}
            color="error"
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Drop Zone for Nested Fields */}
      <Paper
        ref={setDropRef}
        elevation={0}
        sx={{
          bgcolor: isOver ? '#E0F2F1' : 'white',
          p: 2,
          border: isOver ? '3px solid #2e5c66' : '2px dashed #B8DDD0',
          borderRadius: '20px',
          minHeight: '120px',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#3A7380' }}>
            Fields in this line ({nestedFields.length}) - Drag fields here (arrange horizontally)
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddNested}
            sx={{ fontSize: '0.75rem', py: 0.5, px: 1, minHeight: 'auto' }}
          >
            Add Field
          </Button>
        </Box>

        {nestedFields.length === 0 ? (
          <Alert severity="info" sx={{ py: 0.5 }}>
            Drag and drop fields here to display them side by side, or click "Add Field" to create a new field
          </Alert>
        ) : (
          <SortableContext
            items={nestedFields.map((f) => f.name)}
            strategy={horizontalListSortingStrategy}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap', alignItems: 'stretch' }}>
              {nestedFields.map((nestedField, nestedIndex) => (
                <Box
                  key={nestedField.name}
                  sx={{
                    flex: '1 1 200px',
                    minWidth: '200px',
                    maxWidth: '400px',
                  }}
                >
                  <SortableFieldItem
                    field={nestedField}
                    fieldIndex={nestedIndex}
                    parentIndex={fieldIndex}
                    onEdit={onEditNested}
                    onClone={onCloneNested}
                    onDelete={() => onDeleteNested(nestedIndex)}
                    isNested
                  />
                </Box>
              ))}
            </Box>
          </SortableContext>
        )}
      </Paper>
    </ListItem>
  );
};

const NestedLineFieldContainer: React.FC<NestedLineFieldContainerProps> = ({
  field,
  containerIndex,
  lineIndex,
  dropId,
  onAddNestedLineField,
  onEditNested,
  onDeleteNested,
  onEditNestedLineField,
  onDeleteNestedLineField,
}) => {
  // Make the line field sortable within the container
  const {
    attributes: dragAttributes,
    listeners: dragListeners,
    setNodeRef: setDragRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: field.name,
    data: {
      type: 'nested-line-field',
      containerIndex,
      lineIndex,
    }
  });

  // Also make it droppable for nested fields
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: dropId,
    data: {
      type: 'line-in-container',
      containerIndex,
      lineIndex,
    },
  });

  // Combine refs
  const setRefs = (element: HTMLDivElement | null) => {
    setDragRef(element);
    setDropRef(element);
  };

  const lineNestedFields = field.nestedFields || [];

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setRefs}
      style={style}
      elevation={isOver ? 3 : 1}
      sx={{
        border: isOver ? '3px solid #2e5c66' : '2px solid #B8DDD0',
        borderRadius: '12px',
        display: 'block',
        p: 2,
        bgcolor: isOver ? '#B2DFDB' : '#F0F7F8',
        transition: 'all 0.2s ease',
        minHeight: '140px',
        cursor: isDragging ? 'grabbing' : 'pointer',
        position: 'relative',
        '&:hover': {
          bgcolor: '#E0F2F1',
          border: '2px solid #3A7380',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <IconButton
          {...dragAttributes}
          {...dragListeners}
          size="small"
          sx={{ cursor: isDragging ? 'grabbing' : 'grab', mr: 0.5 }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
            {field.label || 'Line Container'}
          </Typography>
          <Chip
            label="Line (Side by Side)"
            size="small"
            sx={{ bgcolor: '#3A7380', color: 'white', fontSize: '0.65rem', height: '18px' }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            ({lineNestedFields.length} fields)
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onAddNestedLineField(containerIndex, lineIndex)}
          sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, minHeight: 'auto', mr: 0.5 }}
        >
          Add Field
        </Button>
        <IconButton
          onClick={() => onEditNested(field, lineIndex, containerIndex)}
          size="small"
          sx={{ p: 0.5, mr: 0.5 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={() => onDeleteNested(lineIndex)}
          color="error"
          size="small"
          sx={{ p: 0.5 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          minHeight: lineNestedFields.length === 0 ? '60px' : '80px',
          p: 1,
          border: isOver ? '2px dashed #2e5c66' : '1px dashed #B8DDD0',
          borderRadius: '8px',
          bgcolor: isOver ? '#E0F2F1' : 'white',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {lineNestedFields.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', py: 1 }}>
            <Typography variant="caption" sx={{ color: '#3A7380', fontSize: '0.75rem', textAlign: 'center' }}>
              Drag fields here to display side by side
            </Typography>
          </Box>
        ) : (
          <SortableContext
            items={lineNestedFields.map((f) => f.name)}
            strategy={horizontalListSortingStrategy}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
              {lineNestedFields.map((lineField, lineFieldIndex) => {
                return (
                  <NestedLineFieldItem
                    key={lineField.name}
                    field={lineField}
                    fieldIndex={lineFieldIndex}
                    containerIndex={containerIndex}
                    lineIndex={lineIndex}
                    onEdit={() => onEditNestedLineField(containerIndex, lineIndex, lineField, lineFieldIndex)}
                    onDelete={() => onDeleteNestedLineField(containerIndex, lineIndex, lineFieldIndex)}
                  />
                );
              })}
            </Box>
            <Box
              sx={{
                minHeight: '40px',
                border: '1px dashed #B8DDD0',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isOver ? '#B2DFDB' : 'transparent',
                mt: 0.5,
              }}
            >
              <Typography variant="caption" sx={{ color: '#3A7380', fontSize: '0.7rem' }}>
                Drop here to add more fields
              </Typography>
            </Box>
          </SortableContext>
        )}
      </Box>
    </Paper>
  );
};

const NestedLineFieldItem: React.FC<{
  field: QuestionField;
  fieldIndex: number;
  containerIndex: number;
  lineIndex: number;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ field, containerIndex, lineIndex, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: field.name,
    data: {
      type: 'triple-nested-field',
      containerIndex,
      lineIndex,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={0}
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        p: 1,
        bgcolor: 'white',
        flex: '1 1 150px',
        minWidth: '150px',
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
          <IconButton
            {...attributes}
            {...listeners}
            size="small"
            sx={{
              cursor: isDragging ? 'grabbing' : 'grab',
              p: 0.25,
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: '0.875rem' }} />
          </IconButton>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
            {field.label}
            {field.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
        </Box>
        <Box>
          <IconButton
            onClick={onEdit}
            size="small"
            sx={{ p: 0.25 }}
          >
            <EditIcon sx={{ fontSize: '0.875rem' }} />
          </IconButton>
          <IconButton
            onClick={onDelete}
            color="error"
            size="small"
            sx={{ p: 0.25 }}
          >
            <DeleteIcon sx={{ fontSize: '0.875rem' }} />
          </IconButton>
        </Box>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
        {field.type}
      </Typography>
    </Paper>
  );
};

const ContainerFieldContainer: React.FC<ContainerFieldContainerProps> = ({
  field,
  fieldIndex,
  onEdit,
  onDelete,
  onEditNested,
  onCloneNested,
  onDeleteNested,
  onAddNested,
  onAddNestedLineField,
  onEditNestedLineField,
  onDeleteNestedLineField,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.name });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `container-${field.name}`,
    data: { type: 'container-container', parentIndex: fieldIndex },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const nestedFields = field.nestedFields || [];

  return (
    <ListItem
      ref={setDragRef}
      style={style}
      sx={{
        mb: 1,
        border: isOver ? '2px solid #2e5c66' : '2px solid #9C27B0',
        borderRadius: '20px',
        display: 'block',
        p: 2,
        bgcolor: isOver ? '#F3E5F5' : '#FAF4FC',
        transition: 'all 0.2s ease',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton
          {...attributes}
          {...listeners}
          size="small"
          sx={{ cursor: isDragging ? 'grabbing' : 'grab', mr: 1 }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {field.label}
              <Chip
                label="Container (Vertical)"
                size="small"
                sx={{ bgcolor: '#9C27B0', color: 'white', fontSize: '0.7rem', height: '20px' }}
              />
            </Box>
          }
          secondary={`Type: ${field.type}${field.required ? ' (Required)' : ''}`}
        />
        <Box>
          <IconButton
            onClick={() => onEdit(field, fieldIndex)}
            sx={{ mr: 1 }}
            size="small"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={onDelete}
            color="error"
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Drop Zone for Nested Fields */}
      <Paper
        ref={setDropRef}
        elevation={0}
        sx={{
          bgcolor: isOver ? '#F3E5F5' : 'white',
          p: 2,
          border: isOver ? '3px solid #9C27B0' : '2px dashed #CE93D8',
          borderRadius: '20px',
          minHeight: '120px',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#9C27B0' }}>
            Fields in this container ({nestedFields.length}) - Drag fields here (arrange vertically)
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddNested}
            sx={{ fontSize: '0.75rem', py: 0.5, px: 1, minHeight: 'auto' }}
          >
            Add Field
          </Button>
        </Box>

        {nestedFields.length === 0 ? (
          <Alert severity="info" sx={{ py: 0.5 }}>
            Drag and drop fields here to display them vertically, or click "Add Field" to create a new field
          </Alert>
        ) : (
          <SortableContext
            items={nestedFields.map((f) => f.name)}
            strategy={verticalListSortingStrategy}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {nestedFields.map((nestedField, nestedIndex) => {
                // Special rendering for line fields inside containers with drag-and-drop support
                if (nestedField.type === 'line') {
                  const lineDropId = `line-in-container-${fieldIndex}-${nestedIndex}-${nestedField.name}`;

                  return (
                    <NestedLineFieldContainer
                      key={nestedField.name}
                      field={nestedField}
                      containerIndex={fieldIndex}
                      lineIndex={nestedIndex}
                      dropId={lineDropId}
                      onAddNestedLineField={onAddNestedLineField}
                      onEditNested={onEditNested}
                      onDeleteNested={onDeleteNested}
                      onEditNestedLineField={onEditNestedLineField}
                      onDeleteNestedLineField={onDeleteNestedLineField}
                    />
                  );
                }

                // Regular field rendering
                return (
                  <SortableFieldItem
                    key={nestedField.name}
                    field={nestedField}
                    fieldIndex={nestedIndex}
                    parentIndex={fieldIndex}
                    onEdit={onEditNested}
                    onClone={onCloneNested}
                    onDelete={() => onDeleteNested(nestedIndex)}
                    isNested
                  />
                );
              })}
            </Box>
          </SortableContext>
        )}
      </Paper>
    </ListItem>
  );
};

// Custom collision detection that prioritizes nested drop zones
const customCollisionDetection: CollisionDetection = (args) => {
  const { active } = args;

  // Get all collisions using pointerWithin for drop zones
  const pointerCollisions = pointerWithin(args);

  // Get all collisions using closestCenter for sortable items
  const centerCollisions = closestCenter(args);

  // Helper to check if an ID is a drop zone
  const isDropZone = (id: string) => {
    return id.startsWith('line-in-container-') || id.startsWith('container-') || id.startsWith('line-');
  };

  // If we have both pointer and center collisions
  if (pointerCollisions.length > 0 && centerCollisions.length > 0) {
    // Separate drop zones from sortable items
    const dropZones = pointerCollisions.filter((c) => isDropZone(String(c.id)));
    const sortableItems = centerCollisions.filter((c) => !isDropZone(String(c.id)));

    // Check if we have sortable items and active data
    const activeData = active.data.current;
    if (activeData && sortableItems.length > 0) {
      // Check if we're hovering over a line-in-container drop zone
      const lineDropZone = dropZones.find((c) => String(c.id).startsWith('line-in-container-'));

      if (lineDropZone) {
        // Parse the line-in-container ID to get container and line indices
        // Format: line-in-container-{containerIndex}-{lineIndex}-{name}
        const lineDropZoneId = String(lineDropZone.id);
        const parts = lineDropZoneId.split('-');

        if (parts.length >= 5) {
          const dropZoneContainerIndex = parseInt(parts[3], 10);
          const dropZoneLineIndex = parseInt(parts[4], 10);

          // Check if the active item is from the same line (should prioritize reordering)
          if (
            activeData.type === 'triple-nested-field' &&
            activeData.containerIndex === dropZoneContainerIndex &&
            activeData.lineIndex === dropZoneLineIndex
          ) {
            // Active item is from the same line - prioritize sortable item for reordering
            return [sortableItems[0]];
          }
        }

        // Active item is NOT from the same line - prioritize drop zone for dropping in
        // This allows triple-nested fields to drop back into their line
        if (activeData.type === 'triple-nested-field') {
          return [lineDropZone];
        }
      }

      // Check if we're reordering container-level nested fields (nested-line-field or nested-field)
      // Only prioritize sortable items if NOT dragging over a line-in-container drop zone
      if (!lineDropZone && (activeData.type === 'nested-line-field' || activeData.type === 'nested-field' || activeData.parentIndex !== undefined)) {
        // Prioritize sortable items for reordering within the same container
        return [sortableItems[0]];
      }
    }

    // Otherwise, prioritize drop zones by specificity
    if (dropZones.length > 0) {
      const prioritized = dropZones.sort((a, b) => {
        const aId = String(a.id);
        const bId = String(b.id);

        // Prioritize line-in-container (most specific)
        if (aId.startsWith('line-in-container-') && !bId.startsWith('line-in-container-')) return -1;
        if (!aId.startsWith('line-in-container-') && bId.startsWith('line-in-container-')) return 1;

        // Then container
        if (aId.startsWith('container-') && !bId.startsWith('container-')) return -1;
        if (!aId.startsWith('container-') && bId.startsWith('container-')) return 1;

        // Then line
        if (aId.startsWith('line-') && !bId.startsWith('line-')) return -1;
        if (!aId.startsWith('line-') && bId.startsWith('line-')) return 1;

        return 0;
      });

      return [prioritized[0]];
    }
  }

  // If we only have pointer collisions, use the original logic
  if (pointerCollisions.length > 0) {
    const prioritized = pointerCollisions.sort((a, b) => {
      const aId = String(a.id);
      const bId = String(b.id);

      // Prioritize line-in-container (most specific)
      if (aId.startsWith('line-in-container-') && !bId.startsWith('line-in-container-')) return -1;
      if (!aId.startsWith('line-in-container-') && bId.startsWith('line-in-container-')) return 1;

      // Then container
      if (aId.startsWith('container-') && !bId.startsWith('container-')) return -1;
      if (!aId.startsWith('container-') && bId.startsWith('container-')) return 1;

      // Then line
      if (aId.startsWith('line-') && !bId.startsWith('line-')) return -1;
      if (!aId.startsWith('line-') && bId.startsWith('line-')) return 1;

      return 0;
    });

    return [prioritized[0]];
  }

  // Fallback to rect intersection if pointer is not within any droppable
  return rectIntersection(args);
};

export const SectionEditor: React.FC<SectionEditorProps> = ({
  section,
  onUpdate,
  allFields = [],
  availablePopups = [],
  questionnaireTypeId,
  questionnaireKind,
  language,
}) => {
  const [fieldEditDialogOpen, setFieldEditDialogOpen] = useState(false);
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<QuestionField | undefined>();
  const [editingParentIndex, setEditingParentIndex] = useState<number | null>(null);
  const [editingLineParentIndex, setEditingLineParentIndex] = useState<number | null>(null); // For triple-nested: container → line → field

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAddField = () => {
    setEditingField(undefined);
    setEditingFieldIndex(null);
    setEditingParentIndex(null);
    setFieldEditDialogOpen(true);
  };

  const handleEditField = (field: QuestionField, fieldIndex: number, parentIndex?: number | null) => {
    setEditingField(field);
    setEditingFieldIndex(fieldIndex);
    setEditingParentIndex(parentIndex ?? null);
    setFieldEditDialogOpen(true);
  };

  const handleCloneField = (field: QuestionField, _fieldIndex: number, parentIndex?: number | null) => {
    setEditingField({ ...field, name: field.name + '_copy' });
    setEditingFieldIndex(null);
    setEditingParentIndex(parentIndex ?? null);
    setEditingLineParentIndex(null);
    setFieldEditDialogOpen(true);
  };

  const handleAddNestedField = (parentIndex: number) => {
    setEditingField(undefined);
    setEditingFieldIndex(null);
    setEditingParentIndex(parentIndex);
    setEditingLineParentIndex(null);
    setFieldEditDialogOpen(true);
  };

  // Handler for adding field inside a line that's inside a container
  const handleAddNestedLineField = (containerIndex: number, lineIndex: number) => {
    setEditingField(undefined);
    setEditingFieldIndex(null);
    setEditingParentIndex(containerIndex);
    setEditingLineParentIndex(lineIndex);
    setFieldEditDialogOpen(true);
  };

  const handleSaveFieldEdit = (updatedField: QuestionField) => {
    setFieldEditDialogOpen(false);
    setEditingField(undefined);
    setEditingFieldIndex(null);

    const newFields = [...section.fields];

    // Check if we're editing a field inside a line that's inside a container (triple-nested)
    if (editingLineParentIndex !== null && editingParentIndex !== null) {
      const containerField = { ...newFields[editingParentIndex] };
      const containerNestedFields = [...(containerField.nestedFields || [])];
      const lineField = { ...containerNestedFields[editingLineParentIndex] };
      const lineNestedFields = [...(lineField.nestedFields || [])];

      if (editingFieldIndex !== null) {
        lineNestedFields[editingFieldIndex] = updatedField;
      } else {
        lineNestedFields.push(updatedField);
      }

      lineField.nestedFields = lineNestedFields;
      containerNestedFields[editingLineParentIndex] = lineField;
      containerField.nestedFields = containerNestedFields;
      newFields[editingParentIndex] = containerField;
    } else if (editingParentIndex !== null && editingParentIndex !== undefined) {
      // Editing nested field (two-level nesting)
      const parentField = { ...newFields[editingParentIndex] };
      const nestedFields = [...(parentField.nestedFields || [])];

      if (editingFieldIndex !== null) {
        nestedFields[editingFieldIndex] = updatedField;
      } else {
        nestedFields.push(updatedField);
      }

      parentField.nestedFields = nestedFields;
      newFields[editingParentIndex] = parentField;
    } else if (editingFieldIndex !== null) {
      // Editing top-level field
      newFields[editingFieldIndex] = updatedField;
    } else {
      // Adding new top-level field
      newFields.push(updatedField);
    }

    onUpdate({ ...section, fields: newFields });
    setEditingParentIndex(null);
    setEditingLineParentIndex(null);
  };

  const handleCloseFieldDialog = () => {
    setFieldEditDialogOpen(false);
    setEditingField(undefined);
    setEditingFieldIndex(null);
    setEditingParentIndex(null);
    setEditingLineParentIndex(null);
  };

  const handleDeleteField = (fieldIndex: number) => {
    onUpdate({
      ...section,
      fields: section.fields.filter((_, i) => i !== fieldIndex),
    });
  };

  const handleDeleteNestedField = (parentIndex: number, nestedIndex: number) => {
    const newFields = [...section.fields];
    const parentField = { ...newFields[parentIndex] };
    const nestedFields = [...(parentField.nestedFields || [])];
    nestedFields.splice(nestedIndex, 1);
    parentField.nestedFields = nestedFields;
    newFields[parentIndex] = parentField;
    onUpdate({ ...section, fields: newFields });
  };

  // Handler for editing a field inside a line that's inside a container
  const handleEditNestedLineField = (
    containerIndex: number,
    lineIndex: number,
    field: QuestionField,
    fieldIndex: number
  ) => {
    setEditingField(field);
    setEditingFieldIndex(fieldIndex);
    setEditingParentIndex(containerIndex);
    setEditingLineParentIndex(lineIndex);
    setFieldEditDialogOpen(true);
  };

  // Handler for deleting a field inside a line that's inside a container
  const handleDeleteNestedLineField = (
    containerIndex: number,
    lineIndex: number,
    fieldIndex: number
  ) => {
    const newFields = [...section.fields];
    const containerField = { ...newFields[containerIndex] };
    const containerNestedFields = [...(containerField.nestedFields || [])];
    const lineField = { ...containerNestedFields[lineIndex] };
    const lineNestedFields = [...(lineField.nestedFields || [])];

    lineNestedFields.splice(fieldIndex, 1);
    lineField.nestedFields = lineNestedFields;
    containerNestedFields[lineIndex] = lineField;
    containerField.nestedFields = containerNestedFields;
    newFields[containerIndex] = containerField;

    onUpdate({ ...section, fields: newFields });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    console.log('🔄 REORDER ATTEMPT:', { activeId, overId });

    // IMPORTANT: Check more specific patterns FIRST before general ones
    // Check if dropping into a line field that's nested inside a container (most specific)
    if (overId.startsWith('line-in-container-')) {
      // Parse the dropId: line-in-container-{containerIndex}-{lineIndex}-{name}
      const parts = overId.split('-');

      if (parts.length >= 4) {
        const containerIndex = parseInt(parts[3], 10);
        const lineIndex = parseInt(parts[4], 10);

        if (isNaN(containerIndex) || isNaN(lineIndex)) return;

        const newFields = [...section.fields];
        const containerField = newFields[containerIndex];

        if (!containerField || !containerField.nestedFields) return;

        const lineField = containerField.nestedFields[lineIndex];

        if (!lineField || lineField.type !== 'line') return;

        // Find source field - only allow from SAME container (hierarchical behavior)
        let sourceField: QuestionField | null = null;
        let sourceNestedIndex: number = -1;
        let sourceIsFromSameContainer = false;
        let sourceIsFromSameLineInContainer = false;
        let sourceLineIndex: number = -1;
        let sourceFieldIndex: number = -1;

        // Check if source is in the SAME container as a direct nested field
        if (containerField.nestedFields) {
          const nestedIdx = containerField.nestedFields.findIndex(f => f.name === activeId);

          if (nestedIdx !== -1) {
            sourceField = containerField.nestedFields[nestedIdx];
            sourceNestedIndex = nestedIdx;
            sourceIsFromSameContainer = true;

            // Don't allow dropping line into another line
            if (sourceField.type === 'line') return;
          }
        }

        // Check if source is in a line field within the SAME container (triple-nested in same container)
        if (!sourceField && containerField.nestedFields) {
          for (let j = 0; j < containerField.nestedFields.length; j++) {
            const lineFieldInContainer = containerField.nestedFields[j];
            if (lineFieldInContainer.type === 'line' && lineFieldInContainer.nestedFields) {
              const tripleNestedIdx = lineFieldInContainer.nestedFields.findIndex(f => f.name === activeId);
              if (tripleNestedIdx !== -1) {
                sourceField = lineFieldInContainer.nestedFields[tripleNestedIdx];
                sourceLineIndex = j;
                sourceFieldIndex = tripleNestedIdx;
                sourceIsFromSameLineInContainer = true;
                break;
              }
            }
          }
        }

        // If source is not from the same container, reject the drop (enforce hierarchical behavior)
        if (!sourceField || (!sourceIsFromSameContainer && !sourceIsFromSameLineInContainer)) return;

        // Remove from source location within the same container
        if (sourceIsFromSameContainer) {
          const updatedContainer = { ...newFields[containerIndex] };
          const updatedContainerNested = [...(updatedContainer.nestedFields || [])];
          updatedContainerNested.splice(sourceNestedIndex, 1);
          updatedContainer.nestedFields = updatedContainerNested;
          newFields[containerIndex] = updatedContainer;

          // Adjust lineIndex if needed (if source was before the target line)
          const adjustedLineIndex = sourceNestedIndex < lineIndex ? lineIndex - 1 : lineIndex;

          // Add to target nested line
          const finalContainer = { ...newFields[containerIndex] };
          const finalContainerNested = [...(finalContainer.nestedFields || [])];
          const finalLine = { ...finalContainerNested[adjustedLineIndex] };
          const finalLineNested = [...(finalLine.nestedFields || [])];
          finalLineNested.push(sourceField);
          finalLine.nestedFields = finalLineNested;
          finalContainerNested[adjustedLineIndex] = finalLine;
          finalContainer.nestedFields = finalContainerNested;
          newFields[containerIndex] = finalContainer;

          onUpdate({ ...section, fields: newFields });
          return;
        } else if (sourceIsFromSameLineInContainer) {
          // Moving field within the same line OR from one line to another within the same container
          const updatedContainer = { ...newFields[containerIndex] };
          const updatedContainerNested = [...(updatedContainer.nestedFields || [])];
          const srcLine = { ...updatedContainerNested[sourceLineIndex] };
          const srcLineNested = [...(srcLine.nestedFields || [])];
          srcLineNested.splice(sourceFieldIndex, 1);
          srcLine.nestedFields = srcLineNested;
          updatedContainerNested[sourceLineIndex] = srcLine;

          // Add to target line
          const targetLine = { ...updatedContainerNested[lineIndex] };
          const targetLineNested = [...(targetLine.nestedFields || [])];
          targetLineNested.push(sourceField);
          targetLine.nestedFields = targetLineNested;
          updatedContainerNested[lineIndex] = targetLine;

          updatedContainer.nestedFields = updatedContainerNested;
          newFields[containerIndex] = updatedContainer;

          onUpdate({ ...section, fields: newFields });
          return;
        }

        // If we reach here, moving from container-level field into line
        onUpdate({ ...section, fields: newFields });
        return;
      }
    }

    // Check if dropping into a top-level line field container (less specific)
    if (overId.startsWith('line-')) {
      const targetLineId = overId.replace('line-', '');
      const targetLineIndex = section.fields.findIndex(f => f.name === targetLineId);

      if (targetLineIndex === -1) return;

      const newFields = [...section.fields];

      // Find source field
      let sourceField: QuestionField | null = null;
      let sourceParentIndex: number | null = null;
      let sourceIndex: number = -1;

      // Check if source is a top-level field
      sourceIndex = newFields.findIndex(f => f.name === activeId);
      if (sourceIndex !== -1) {
        sourceField = newFields[sourceIndex];

        // Don't allow dropping a line field into another line field
        if (sourceField.type === 'line' || sourceField.type === 'container') return;

        // Remove from top level
        newFields.splice(sourceIndex, 1);

        // Adjust target index if needed
        const adjustedTargetIndex = sourceIndex < targetLineIndex ? targetLineIndex - 1 : targetLineIndex;
        const adjustedTargetLine = { ...newFields[adjustedTargetIndex] };

        // Add to target line
        adjustedTargetLine.nestedFields = [...(adjustedTargetLine.nestedFields || []), sourceField];
        newFields[adjustedTargetIndex] = adjustedTargetLine;

        onUpdate({ ...section, fields: newFields });
        return;
      }

      // Check if source is a nested field
      for (let i = 0; i < newFields.length; i++) {
        if (newFields[i].nestedFields) {
          const nestedIndex = newFields[i].nestedFields!.findIndex(f => f.name === activeId);
          if (nestedIndex !== -1) {
            sourceField = newFields[i].nestedFields![nestedIndex];
            sourceParentIndex = i;
            sourceIndex = nestedIndex;
            break;
          }
        }
      }

      if (sourceField && sourceParentIndex !== null) {
        // Moving from one line to another
        const sourceParent = { ...newFields[sourceParentIndex] };
        const sourceNested = [...(sourceParent.nestedFields || [])];
        sourceNested.splice(sourceIndex, 1);
        sourceParent.nestedFields = sourceNested;
        newFields[sourceParentIndex] = sourceParent;

        // Add to target line
        const adjustedTargetIndex = sourceParentIndex < targetLineIndex ? targetLineIndex - 1 : targetLineIndex;
        const adjustedTargetLine = { ...newFields[adjustedTargetIndex] };
        adjustedTargetLine.nestedFields = [...(adjustedTargetLine.nestedFields || []), sourceField];
        newFields[adjustedTargetIndex] = adjustedTargetLine;

        onUpdate({ ...section, fields: newFields });
      }
      return;
    }

    // Check if dropping into a container field
    if (overId.startsWith('container-')) {
      const targetContainerId = overId.replace('container-', '');
      const targetContainerIndex = section.fields.findIndex(f => f.name === targetContainerId);

      if (targetContainerIndex === -1) return;

      const newFields = [...section.fields];

      // Find source field
      let sourceField: QuestionField | null = null;
      let sourceParentIndex: number | null = null;
      let sourceIndex: number = -1;

      // Check if source is a top-level field
      sourceIndex = newFields.findIndex(f => f.name === activeId);
      if (sourceIndex !== -1) {
        sourceField = newFields[sourceIndex];

        // Don't allow dropping a container field into another container (but allow line fields)
        if (sourceField.type === 'container') return;

        // Remove from top level
        newFields.splice(sourceIndex, 1);

        // Adjust target index if needed
        const adjustedTargetIndex = sourceIndex < targetContainerIndex ? targetContainerIndex - 1 : targetContainerIndex;
        const adjustedTargetContainer = { ...newFields[adjustedTargetIndex] };

        // Add to target container
        adjustedTargetContainer.nestedFields = [...(adjustedTargetContainer.nestedFields || []), sourceField];
        newFields[adjustedTargetIndex] = adjustedTargetContainer;

        onUpdate({ ...section, fields: newFields });
        return;
      }

      // Check if source is a nested field (double-nested)
      for (let i = 0; i < newFields.length; i++) {
        if (newFields[i].nestedFields) {
          const nestedIndex = newFields[i].nestedFields!.findIndex(f => f.name === activeId);
          if (nestedIndex !== -1) {
            sourceField = newFields[i].nestedFields![nestedIndex];
            sourceParentIndex = i;
            sourceIndex = nestedIndex;
            break;
          }
        }
      }

      // Check if source is a triple-nested field (inside line inside container)
      let sourceTripleNested: { containerIdx: number; lineIdx: number; fieldIdx: number } | null = null;
      if (!sourceField) {
        for (let i = 0; i < newFields.length; i++) {
          if (newFields[i].nestedFields) {
            for (let j = 0; j < newFields[i].nestedFields!.length; j++) {
              const lineFieldInContainer = newFields[i].nestedFields![j];
              if (lineFieldInContainer.type === 'line' && lineFieldInContainer.nestedFields) {
                const tripleNestedIdx = lineFieldInContainer.nestedFields.findIndex(f => f.name === activeId);
                if (tripleNestedIdx !== -1) {
                  sourceField = lineFieldInContainer.nestedFields[tripleNestedIdx];
                  sourceTripleNested = { containerIdx: i, lineIdx: j, fieldIdx: tripleNestedIdx };
                  break;
                }
              }
            }
            if (sourceTripleNested) break;
          }
        }
      }

      if (sourceField && sourceParentIndex !== null) {
        // Moving from one container/line to another (double-nested source)
        const sourceParent = { ...newFields[sourceParentIndex] };
        const sourceNested = [...(sourceParent.nestedFields || [])];
        sourceNested.splice(sourceIndex, 1);
        sourceParent.nestedFields = sourceNested;
        newFields[sourceParentIndex] = sourceParent;

        // Add to target container
        const adjustedTargetIndex = sourceParentIndex < targetContainerIndex ? targetContainerIndex - 1 : targetContainerIndex;
        const adjustedTargetContainer = { ...newFields[adjustedTargetIndex] };
        adjustedTargetContainer.nestedFields = [...(adjustedTargetContainer.nestedFields || []), sourceField];
        newFields[adjustedTargetIndex] = adjustedTargetContainer;

        onUpdate({ ...section, fields: newFields });
      } else if (sourceField && sourceTripleNested !== null) {
        // Moving from triple-nested (line inside container) to container
        // Only allow if dropping into the SAME container
        if (sourceTripleNested.containerIdx !== targetContainerIndex) return;

        // Remove from line
        const container = { ...newFields[sourceTripleNested.containerIdx] };
        const containerNested = [...(container.nestedFields || [])];
        const lineField = { ...containerNested[sourceTripleNested.lineIdx] };
        const lineNested = [...(lineField.nestedFields || [])];
        lineNested.splice(sourceTripleNested.fieldIdx, 1);
        lineField.nestedFields = lineNested;
        containerNested[sourceTripleNested.lineIdx] = lineField;

        // Add to container at the end
        containerNested.push(sourceField);
        container.nestedFields = containerNested;
        newFields[sourceTripleNested.containerIdx] = container;

        onUpdate({ ...section, fields: newFields });
      }
      return;
    }

    // Check if reordering within triple-nested fields (line inside container)
    // Find if both active and over are in the same nested line field
    let activeTripleNested: { containerIdx: number; lineIdx: number; fieldIdx: number } | null = null;
    let overTripleNested: { containerIdx: number; lineIdx: number; fieldIdx: number } | null = null;

    for (let i = 0; i < section.fields.length; i++) {
      if (section.fields[i].nestedFields) {
        for (let j = 0; j < section.fields[i].nestedFields!.length; j++) {
          const lineFieldInContainer = section.fields[i].nestedFields![j];
          if (lineFieldInContainer.type === 'line' && lineFieldInContainer.nestedFields) {
            const activeIdx = lineFieldInContainer.nestedFields.findIndex(f => f.name === activeId);
            if (activeIdx !== -1) {
              activeTripleNested = { containerIdx: i, lineIdx: j, fieldIdx: activeIdx };
            }
            const overIdx = lineFieldInContainer.nestedFields.findIndex(f => f.name === overId);
            if (overIdx !== -1) {
              overTripleNested = { containerIdx: i, lineIdx: j, fieldIdx: overIdx };
            }
          }
        }
      }
    }

    console.log('🔍 TRIPLE-NESTED DETECTION:', { activeTripleNested, overTripleNested });

    // If both are in the same nested line field, reorder them
    if (
      activeTripleNested &&
      overTripleNested &&
      activeTripleNested.containerIdx === overTripleNested.containerIdx &&
      activeTripleNested.lineIdx === overTripleNested.lineIdx &&
      activeId !== overId
    ) {
      console.log('✅ REORDERING TRIPLE-NESTED:', {
        activeTripleNested,
        overTripleNested,
        from: activeTripleNested.fieldIdx,
        to: overTripleNested.fieldIdx,
      });

      const newFields = [...section.fields];
      const containerField = { ...newFields[activeTripleNested.containerIdx] };
      const containerNested = [...(containerField.nestedFields || [])];
      const lineField = { ...containerNested[activeTripleNested.lineIdx] };
      const lineNested = [...(lineField.nestedFields || [])];

      const reordered = arrayMove(lineNested, activeTripleNested.fieldIdx, overTripleNested.fieldIdx);
      lineField.nestedFields = reordered;
      containerNested[activeTripleNested.lineIdx] = lineField;
      containerField.nestedFields = containerNested;
      newFields[activeTripleNested.containerIdx] = containerField;

      onUpdate({ ...section, fields: newFields });
      return;
    }

    // Check if dragging a triple-nested field out to container level (hierarchical behavior)
    if (activeTripleNested && activeId !== overId) {
      const newFields = [...section.fields];
      const containerField = newFields[activeTripleNested.containerIdx];

      // Check if over target is a nested field in the SAME container
      let overIsInSameContainer = false;
      let overNestedIndex = -1;

      if (containerField.nestedFields) {
        overNestedIndex = containerField.nestedFields.findIndex(f => f.name === overId);
        if (overNestedIndex !== -1) {
          overIsInSameContainer = true;
        }
      }

      // Only allow dropping into the same container (enforce hierarchical behavior)
      if (!overIsInSameContainer) return;

      // Get the triple-nested field being moved
      const updatedContainer = { ...containerField };
      const containerNested = [...(updatedContainer.nestedFields || [])];
      const lineField = { ...containerNested[activeTripleNested.lineIdx] };
      const lineNested = [...(lineField.nestedFields || [])];
      const [movedField] = lineNested.splice(activeTripleNested.fieldIdx, 1);

      // Update the nested line field
      lineField.nestedFields = lineNested;
      containerNested[activeTripleNested.lineIdx] = lineField;

      // Insert at target position within the container
      containerNested.splice(overNestedIndex, 0, movedField);
      updatedContainer.nestedFields = containerNested;
      newFields[activeTripleNested.containerIdx] = updatedContainer;

      onUpdate({ ...section, fields: newFields });
      return;
    }

    // Check if reordering within nested fields (horizontal reordering)
    // Find if both active and over are nested fields in the same parent
    let activeParentIndex: number = -1;
    let activeNestedIndex: number = -1;
    let overParentIndex: number = -1;
    let overNestedIndex: number = -1;

    for (let i = 0; i < section.fields.length; i++) {
      if (section.fields[i].nestedFields) {
        const activeIdx = section.fields[i].nestedFields!.findIndex(f => f.name === activeId);
        if (activeIdx !== -1) {
          activeParentIndex = i;
          activeNestedIndex = activeIdx;
        }
        const overIdx = section.fields[i].nestedFields!.findIndex(f => f.name === overId);
        if (overIdx !== -1) {
          overParentIndex = i;
          overNestedIndex = overIdx;
        }
      }
    }

    // If both are in the same parent line field, reorder horizontally
    if (activeParentIndex !== -1 && overParentIndex !== -1 && activeParentIndex === overParentIndex && activeId !== overId) {
      const newFields = [...section.fields];
      const parentField = { ...newFields[activeParentIndex] };
      const nestedFields = [...(parentField.nestedFields || [])];
      const reordered = arrayMove(nestedFields, activeNestedIndex, overNestedIndex);
      parentField.nestedFields = reordered;
      newFields[activeParentIndex] = parentField;
      onUpdate({ ...section, fields: newFields });
      return;
    }

    // Check if dragging a nested field out to top level
    // Find if active is a nested field
    let activeNestParentIndex: number = -1;
    let activeNestIndex: number = -1;

    for (let i = 0; i < section.fields.length; i++) {
      if (section.fields[i].nestedFields) {
        const idx = section.fields[i].nestedFields!.findIndex(f => f.name === activeId);
        if (idx !== -1) {
          activeNestParentIndex = i;
          activeNestIndex = idx;
          break;
        }
      }
    }

    // If active is nested and over is top-level, move it out
    if (activeNestParentIndex !== -1 && activeId !== overId) {
      const overIndex = section.fields.findIndex(f => f.name === overId);
      if (overIndex !== -1) {
        const newFields = [...section.fields];

        // Get the nested field being moved
        const parentField = { ...newFields[activeNestParentIndex] };
        const nestedFields = [...(parentField.nestedFields || [])];
        const [movedField] = nestedFields.splice(activeNestIndex, 1);

        // Update parent field
        parentField.nestedFields = nestedFields;
        newFields[activeNestParentIndex] = parentField;

        // Insert at target position
        newFields.splice(overIndex, 0, movedField);

        onUpdate({ ...section, fields: newFields });
        return;
      }
    }

    // Regular reordering logic (top-level fields)
    if (activeId !== overId) {
      // Check if both are top-level fields
      const activeIndex = section.fields.findIndex(f => f.name === activeId);
      const overIndex = section.fields.findIndex(f => f.name === overId);

      if (activeIndex !== -1 && overIndex !== -1) {
        const reordered = arrayMove(section.fields, activeIndex, overIndex);
        onUpdate({ ...section, fields: reordered });
      }
    }
  };

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Questions in this section</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddField}
          >
            Add Question
          </Button>
        </Box>

        {section.fields.length === 0 ? (
          <Alert severity="info">
            No questions yet. Add one to get started.
          </Alert>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={customCollisionDetection}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={section.fields.map((f) => f.name)}
              strategy={verticalListSortingStrategy}
            >
              <List sx={{ bgcolor: 'background.paper' }}>
                {section.fields.map((field, fieldIndex) => {
                  if (field.type === 'line') {
                    return (
                      <LineFieldContainer
                        key={field.name}
                        field={field}
                        fieldIndex={fieldIndex}
                        onEdit={handleEditField}
                        onDelete={() => handleDeleteField(fieldIndex)}
                        onEditNested={(f, ni, pi) => handleEditField(f, ni, pi)}
                        onCloneNested={(f, ni, pi) => handleCloneField(f, ni, pi)}
                        onDeleteNested={(ni) => handleDeleteNestedField(fieldIndex, ni)}
                        onAddNested={() => handleAddNestedField(fieldIndex)}
                      />
                    );
                  }
                  if (field.type === 'container') {
                    return (
                      <ContainerFieldContainer
                        key={field.name}
                        field={field}
                        fieldIndex={fieldIndex}
                        onEdit={handleEditField}
                        onDelete={() => handleDeleteField(fieldIndex)}
                        onEditNested={(f, ni, pi) => handleEditField(f, ni, pi)}
                        onCloneNested={(f, ni, pi) => handleCloneField(f, ni, pi)}
                        onDeleteNested={(ni) => handleDeleteNestedField(fieldIndex, ni)}
                        onAddNested={() => handleAddNestedField(fieldIndex)}
                        onAddNestedLineField={handleAddNestedLineField}
                        onEditNestedLineField={handleEditNestedLineField}
                        onDeleteNestedLineField={handleDeleteNestedLineField}
                      />
                    );
                  }
                  return (
                    <SortableFieldItem
                      key={field.name}
                      field={field}
                      fieldIndex={fieldIndex}
                      onEdit={handleEditField}
                      onClone={handleCloneField}
                      onDelete={() => handleDeleteField(fieldIndex)}
                    />
                  );
                })}
              </List>
            </SortableContext>
          </DndContext>
        )}
      </Box>

      {/* Field Edit Dialog */}
      <FieldEditDialog
        open={fieldEditDialogOpen}
        field={editingField}
        onSave={handleSaveFieldEdit}
        onCancel={handleCloseFieldDialog}
        availableFields={allFields}
        availablePopups={availablePopups}
        questionnaireTypeId={questionnaireTypeId}
        questionnaireKind={questionnaireKind}
        language={language}
      />
    </>
  );
};
